// src/Page/UrlClassificationPage.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import SideBar from "../Component/SideBar";
import useUrlClassificationApi from "../Api/useUrlClassificationApi";
import "../styles/styles.css";
import { CATEGORY_LIST } from "../Component/CategoryList";


const UrlClassificationPage = () => {
  const { classifyUrl, result, loading } = useUrlClassificationApi();
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  const handleClassify = () => {
    if (!url.trim()) {
      setError("Please enter a valid URL");
      return;
    }
    setError("");
    classifyUrl({ url });
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Sidebar */}
      <SideBar />

      {/* Main Content */}
      <Box className="main-box" sx={{ px: 3, py: 4 }}>
        {/* Page Title */}
        <Typography
          variant="h4"
          fontWeight="700"
          mb={4}
          sx={{ textAlign: { xs: "center", sm: "left" } }}
        >
          Classify URL
        </Typography>

        {/* Input Section */}
        <Paper
          sx={{
            p: { xs: 2, sm: 4 },
            mb: 3,
            borderRadius: 4,
            backdropFilter: "blur(12px)",
            background: "rgba(255,255,255,0.85)",
            boxShadow: "0px 12px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="h6" fontWeight="600" mb={2}>
            Enter URL to Classify 
          </Typography>

          <TextField
            label="URL"
            placeholder="https://example.com"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!!error}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1, ml: 1 }}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleClassify}
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(135deg,#4a90e2,#007aff)",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Classify URL"
            )}
          </Button>
        </Paper>

        {/* Result Section */}
        <Card
          sx={{
            borderRadius: 4,
            p: { xs: 2, sm: 3 },
            boxShadow: "0px 12px 30px rgba(0,0,0,0.08)",
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CardContent>
            <Typography variant="h5" fontWeight="700" mb={2}>
              Results
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* Loading */}
            {loading && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Processing your URL…
                </Typography>
              </Box>
            )}

            {/* Result */}
            {/* Result */}
            {!loading && result?.categories?.length > 0 && (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {result.categories.map((catId, idx) => {
                    const category = CATEGORY_LIST.find((c) => c.id === catId);
                    return (
                      <Chip
                        key={idx}
                        label={`(${catId}) ${
                          category ? category.name : "Unknown"
                        }`} // Show ID and Name
                        color="primary"
                        sx={{
                          px: 2,
                          py: 1,
                          fontSize: "0.9rem",
                          borderRadius: 2,
                          background: "#e3f2fd",
                          color: "#0d47a1",
                          fontWeight: 600,
                        }}
                      />
                    );
                  })}
                </Box>
              </Stack>
            )}

            {/* No Result */}
            {!loading && (!result || result.categories?.length === 0) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                Enter a URL above and click "Classify URL" to view results.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default UrlClassificationPage;
